import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react"
import { PianoUser, LoggedInObject } from "../Quiz/Result/use_pianoid"

export interface IPianoContext {
  userInfo?: { user: PianoUser, token?: string }
  state: "initializing" | "initialized" | "none"
  loginCooldown: boolean,
  forceLogin(onSuccess: (user: PianoUser, token: string) => void): void
}

export const PianoContext = React.createContext<IPianoContext>({
  forceLogin: () => {},
  loginCooldown: false,
  state: "none",
})

const getTp = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).tp = window.tp || []
  return window.tp as {
    push: (param: [name: string, callback: () => void]) => void,
    pianoId: {
      getUser: () => PianoUser | null,
      getToken: () => string | null,
      show: (options: {
        loggedIn: (data: LoggedInObject) => void
      }) => void
    }
  }
}

export const WithConnectedPiano: React.FC<React.PropsWithChildren<{initPlaceholder?: ReactNode}>> = ({ children, initPlaceholder }) => {
  const [pianoState, setPianoState] = useState<Omit<IPianoContext, "forceLogin">>({
    loginCooldown: false,
    state: "none"
  })

  useEffect(() => {
    if (pianoState.state === "none") {
      setPianoState({ loginCooldown: false, state: "initializing" })
      getTp().push(["init", () => {
        const user = getTp().pianoId.getUser()
        const token = getTp().pianoId.getToken()
        setPianoState({
          loginCooldown: false,
          state: "initialized",
          userInfo: user ? { user, token: token || undefined } : undefined,
        })
      }])
    }
  }, [pianoState.state])

  const forceLogin = useCallback<IPianoContext["forceLogin"]>((onSuccess) => {
    setPianoState((prev) => ({
      ...prev,
      loginCooldown: true,
    }))
    getTp().push(["init", () => {
      const timeout = window.setTimeout(() => {
        setPianoState((prev) => ({
          ...prev,
          loginCooldown: false,
        }))
      }, 2500)
      getTp().pianoId.show({
        loggedIn: (data) => {
          window.clearTimeout(timeout)
          if (data.user && data.token) {
            setPianoState((prev) => ({
              ...prev,
              loginCooldown: false,
              userInfo: { user: data.user!, token: data.token! },
            }))
            onSuccess(data.user!, data.token!)
          } else {
            console.warn("Piano loggedIn was executed, but didn't contain user + token", data)
            setPianoState((prev) => ({
              ...prev,
              loginCooldown: false,
            }))
          }
        }
      })
    }])
  }, [])

  const contextVal = useMemo<IPianoContext>(() => {
    return {
      ...pianoState,
      forceLogin,
    }
  }, [forceLogin, pianoState])

  if (initPlaceholder && pianoState.state !== "initialized") {
    return <>{initPlaceholder}</>
  }

  return <PianoContext.Provider value={contextVal}>{children}</PianoContext.Provider>
}

export const usePiano = () => useContext(PianoContext)
